import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  HStack,
  Badge,
  Code,
  Box,
  Divider,
  Flex,
  Button,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { RefreshIcon, useToastHook } from '@devd-client/devd/components';
import { useSetIntegrationInactive, useResetAccountState } from './api';

interface AccountStateAudit {
  state: string;
  status: string;
  handler: string;
  context: string | null;
  lastExecutionTime: string | null;
  updatedAt: string;
}

export interface IntegrationDetail {
  integrationType: string;
  integration: string;
  integrationKey: string;
  integrationName: string;
  apiKey: string;
  updatedAt: string;
  accountStateAudits: AccountStateAudit[];
}

interface IntegrationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: IntegrationDetail | null;
  orgId: string;
  isLoading?: boolean;
}

export function IntegrationDetailsModal({
  isOpen,
  onClose,
  integration,
  orgId,
  isLoading = false,
}: IntegrationDetailsModalProps) {
  const [newToast] = useToastHook();
  const [confirmReset, setConfirmReset] = useState<{
    open: boolean;
    state?: string;
  }>({ open: false });
  const [confirmInactiveOpen, setConfirmInactiveOpen] = useState(false);
  const cancelRef = useRef<any>(null);

  const {
    mutate: setIntegrationInactiveMutation,
    isLoading: isSettingInactive,
  } = useSetIntegrationInactive();
  const { mutate: resetAccountStateMutation, isLoading: isResetting } =
    useResetAccountState();

  const handleSetInactive = () => {
    if (!integration) return;
    setIntegrationInactiveMutation(
      {
        orgId,
        integrationType: integration.integrationType,
        integration: integration.integration,
      },
      {
        onSuccess: () => {
          newToast({
            message: 'Integration set to inactive',
            status: 'success',
          });
          setConfirmInactiveOpen(false);
        },
        onError: () => {
          newToast({ message: 'Failed to set inactive', status: 'error' });
        },
      }
    );
  };

  const handleConfirmReset = () => {
    if (!integration) return;
    resetAccountStateMutation(
      {
        orgId,
        integrationKey: integration.integrationKey,
        integrationType: integration.integrationType,
        state: confirmReset.state as string,
      },
      {
        onSuccess: () => {
          newToast({ message: 'State reset initiated', status: 'success' });
          setConfirmReset({ open: false });
        },
        onError: () => {
          newToast({ message: 'Failed to reset state', status: 'error' });
        },
      }
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        motionPreset="slideInBottom"
        scrollBehavior="inside"
        size={{ base: 'full', md: 'xl', lg: '4xl' }}
      >
        <ModalOverlay />
        <ModalContent
          borderRadius="2px"
          boxShadow="0px 0px 14px rgba(0, 0, 0, 0.1)"
          maxH="90vh"
          minW={{ base: '100%', md: '600px', lg: '900px' }}
        >
          <ModalHeader
            fontFamily="heading"
            fontWeight="bold"
            lineHeight="22px"
            color="text.primary"
            pb={2}
            px={4}
          >
            {isLoading || !integration ? (
              <Text>Integration Details</Text>
            ) : (
              <Flex direction="column" gap={2}>
                <Flex align="center" gap={3} flexWrap="wrap">
                  <Text>Integration Details</Text>
                  <Text
                    bg="primary"
                    color="white"
                    px={2}
                    py={1}
                    borderRadius="4px"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {integration.integration === 'cloud'
                      ? integration.integrationType === 'git'
                        ? 'GitHub'
                        : 'Jira'
                      : integration.integration.charAt(0).toUpperCase() +
                        integration.integration.slice(1)}
                  </Text>
                  <Text
                    fontFamily="heading"
                    fontSize="xs"
                    color="text.secondary"
                    fontWeight="normal"
                  >
                    {integration.integrationType}
                  </Text>
                  <Button
                    ml="auto"
                    mr={8}
                    size="sm"
                    variant="custom-outline"
                    isLoading={isSettingInactive}
                    onClick={() => setConfirmInactiveOpen(true)}
                  >
                    Set Inactive
                  </Button>
                </Flex>
                <Text
                  fontFamily="heading"
                  fontSize="xs"
                  color="text.secondary"
                  fontWeight="normal"
                  lineHeight="17px"
                >
                  Updated: {new Date(integration.updatedAt).toLocaleString()}
                </Text>
              </Flex>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody px={4} pb={6}>
            {isLoading || !integration ? (
              <Center minH="200px">
                <Spinner size="xl" />
              </Center>
            ) : (
              <VStack align="stretch" spacing={4}>
                {integration.accountStateAudits &&
                  integration.accountStateAudits.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Text
                          fontFamily="heading"
                          fontWeight="bold"
                          fontSize="sm"
                          color="text.primary"
                          mb={2}
                        >
                          Account State Audits:
                        </Text>
                        <VStack
                          align="stretch"
                          spacing={2}
                          maxH="400px"
                          overflowY="auto"
                        >
                          {integration.accountStateAudits.map(
                            (audit, index) => (
                              <Box
                                key={index}
                                p={3}
                                borderWidth="1px"
                                borderRadius="4px"
                                borderColor="gray.200"
                                bg="gray.50"
                              >
                                <HStack justify="space-between" mb={2}>
                                  <HStack>
                                    <Badge
                                      colorScheme={
                                        audit.status === 'FINISHED'
                                          ? 'green'
                                          : 'yellow'
                                      }
                                    >
                                      {audit.state}
                                    </Badge>
                                    <IconButton
                                      aria-label="Reset state"
                                      size="xs"
                                      variant="ghost"
                                      icon={
                                        <RefreshIcon width={14} height={14} />
                                      }
                                      onClick={() =>
                                        setConfirmReset({
                                          open: true,
                                          state: audit.state,
                                        })
                                      }
                                    />
                                  </HStack>
                                  <Badge>{audit.status}</Badge>
                                </HStack>
                                <Text
                                  fontFamily="heading"
                                  fontSize="xs"
                                  color="text.secondary"
                                  lineHeight="17px"
                                >
                                  Handler: {audit.handler}
                                </Text>
                                {audit.lastExecutionTime && (
                                  <Text
                                    fontFamily="heading"
                                    fontSize="xs"
                                    color="text.secondary"
                                    lineHeight="17px"
                                  >
                                    Last Execution:{' '}
                                    {new Date(
                                      audit.lastExecutionTime
                                    ).toLocaleString()}
                                  </Text>
                                )}
                                <Text
                                  fontFamily="heading"
                                  fontSize="xs"
                                  color="text.secondary"
                                  lineHeight="17px"
                                >
                                  Updated:{' '}
                                  {new Date(audit.updatedAt).toLocaleString()}
                                </Text>
                                {audit.context && (
                                  <Box mt={2}>
                                    <Text
                                      fontFamily="heading"
                                      fontSize="xs"
                                      fontWeight="semibold"
                                      color="text.primary"
                                      mb={1}
                                    >
                                      Context:
                                    </Text>
                                    <Code
                                      p={1}
                                      fontSize="xs"
                                      display="block"
                                      whiteSpace="pre-wrap"
                                      fontFamily="mono"
                                      bg="white"
                                      borderRadius="4px"
                                    >
                                      {typeof audit.context === 'string'
                                        ? audit.context
                                        : JSON.stringify(
                                            audit.context,
                                            null,
                                            2
                                          )}
                                    </Code>
                                  </Box>
                                )}
                              </Box>
                            )
                          )}
                        </VStack>
                      </Box>
                    </>
                  )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={confirmReset.open}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          if (!isResetting) {
            setConfirmReset({ open: false });
          }
        }}
        closeOnOverlayClick={!isResetting}
        closeOnEsc={!isResetting}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reset State?
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to reset the state "{confirmReset.state}" to
              INIT?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setConfirmReset({ open: false })}
                isDisabled={isResetting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirmReset}
                isLoading={isResetting}
                loadingText="Resetting..."
                isDisabled={isResetting}
                ml={3}
              >
                Yes, Reset
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={confirmInactiveOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          if (!isSettingInactive) {
            setConfirmInactiveOpen(false);
          }
        }}
        closeOnOverlayClick={!isSettingInactive}
        closeOnEsc={!isSettingInactive}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Set Integration Inactive?
            </AlertDialogHeader>
            <AlertDialogBody>
              This will set "{integration?.integrationType}" to inactive for
              this organization.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setConfirmInactiveOpen(false)}
                isDisabled={isSettingInactive}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSetInactive}
                isLoading={isSettingInactive}
                loadingText="Setting Inactive..."
                isDisabled={isSettingInactive}
                ml={3}
              >
                Yes, Set Inactive
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
